/*
 * Signed EAs tab — Oct 1, 2026
 * -----------------------------------------------------------------
 * Dedicated space for the Registrar + President to see every student
 * whose Enrollment Agreement has been signed (DocuSign OR manually
 * uploaded), grouped by intake, with per-student download + bulk ZIP
 * download per intake.
 *
 * Backend contracts:
 *   GET  /institution/signed-eas → { total, groups: [{ intake_label,
 *          students, count, downloadable_count }] }
 *   POST /institution/signed-eas/bulk-zip (body: { student_ids })
 *          → { job_id, filename, total }
 *   GET  /institution/students/bulk-download-zip-jobs/{id}
 *   GET  /institution/students/bulk-download-zip-jobs/{id}/download?t=
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DownloadSimple, FileText, CheckCircle, CircleNotch, UploadSimple,
  CaretDown, CaretRight, Archive, WarningCircle,
} from '@phosphor-icons/react';
import { API } from '@/config/api';

export default function SignedEAsTab() {
  const [data, setData] = useState({ total: 0, groups: [] });
  const [loading, setLoading] = useState(true);
  const [openGroups, setOpenGroups] = useState({}); // label → bool
  // Active bulk-zip job per intake label (so each intake's packaging
  // is independent — one failing doesn't block another).
  const [zipJobs, setZipJobs] = useState({}); // label → { state, done, total, download_url, error }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/institution/signed-eas`);
      setData(res || { total: 0, groups: [] });
      // Open first intake by default so the page isn't empty.
      if (res?.groups?.length) {
        setOpenGroups(prev => Object.keys(prev).length ? prev : { [res.groups[0].intake_label]: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load signed EAs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadOne = async (student) => {
    try {
      const resp = await axios.get(
        `${API}/institution/students/${student.id}/ea-signed-pdf`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `EA_signed_${(student.full_name || 'student').replace(/[^a-zA-Z0-9._-]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let detail = '';
      const body = err?.response?.data;
      if (body instanceof Blob) {
        try { detail = JSON.parse(await body.text()).detail; } catch { /* ignore */ }
      } else if (body?.detail) { detail = body.detail; }
      toast.error(detail || 'Failed to download signed EA');
    }
  };

  const uploadOne = async (student, file) => {
    if (!file) return;
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(
        `${API}/institution/students/${student.id}/ea-signed-pdf-upload`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('Signed EA uploaded');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
  };

  const bulkDownloadIntake = async (group) => {
    // Only queue the downloadable ones — students with no PDF on file
    // would just error inside the job.
    const ids = group.students.filter(s => s.has_downloadable_ea).map(s => s.id);
    if (!ids.length) {
      toast.error('No downloadable EAs in this intake. Upload a signed PDF on each first.');
      return;
    }
    setZipJobs(prev => ({ ...prev, [group.intake_label]: { state: 'queued', done: 0, total: ids.length } }));
    try {
      const { data: kicked } = await axios.post(`${API}/institution/signed-eas/bulk-zip`, { student_ids: ids });
      // Poll.
      const deadline = Date.now() + 10 * 60 * 1000; // 10 min
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 2000));
        const { data: status } = await axios.get(`${API}/institution/students/bulk-download-zip-jobs/${kicked.job_id}`);
        setZipJobs(prev => ({ ...prev, [group.intake_label]: status }));
        if (status.state === 'done') {
          const url = `${API}/institution/students/bulk-download-zip-jobs/${kicked.job_id}/download?t=${status.download_token}`;
          const a = document.createElement('a');
          a.href = url;
          a.download = status.filename || `signed_eas_${group.intake_label}.zip`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          toast.success(`${status.packed || status.done} signed EA${(status.packed || status.done) === 1 ? '' : 's'} packaged`);
          return;
        }
        if (status.state === 'failed') {
          toast.error(status.error || 'Packaging failed');
          return;
        }
      }
      toast.error('Packaging timed out — please retry');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start bulk download');
      setZipJobs(prev => { const n = { ...prev }; delete n[group.intake_label]; return n; });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500" data-testid="signed-eas-loading">
        <CircleNotch size={16} className="animate-spin mr-2" /> Loading signed EAs…
      </div>
    );
  }

  if (!data.groups.length) {
    return (
      <div className="text-center py-16 text-slate-500" data-testid="signed-eas-empty">
        <FileText size={28} className="mx-auto mb-3 text-slate-300" />
        <p className="text-sm font-medium">No signed EAs yet</p>
        <p className="text-xs mt-1">Students appear here once their EA is signed via DocuSign or manually marked.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="signed-eas-tab">
      {/* Header strip */}
      <div className="flex items-center justify-between gap-3 pb-3 border-b border-slate-200">
        <div>
          <h3 className="text-[13px] font-semibold uppercase tracking-wider text-slate-500">Signed Enrollment Agreements</h3>
          <p className="text-[20px] font-semibold text-slate-900 mt-0.5 tabular-nums">{data.total} <span className="text-[13px] font-normal text-slate-500">signed · across {data.groups.length} intake{data.groups.length === 1 ? '' : 's'}</span></p>
        </div>
      </div>

      {/* One accordion per intake */}
      {data.groups.map(g => {
        const open = !!openGroups[g.intake_label];
        const job = zipJobs[g.intake_label];
        const busy = job && (job.state === 'queued' || job.state === 'running');
        return (
          <section
            key={g.intake_label}
            className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm"
            data-testid={`signed-ea-group-${g.intake_label.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
          >
            <header
              className="flex items-center justify-between gap-3 px-5 py-3.5 cursor-pointer hover:bg-slate-50 transition"
              onClick={() => setOpenGroups(prev => ({ ...prev, [g.intake_label]: !open }))}
            >
              <div className="flex items-center gap-3 min-w-0">
                {open ? <CaretDown size={14} className="text-slate-500 shrink-0" /> : <CaretRight size={14} className="text-slate-500 shrink-0" />}
                <h4 className="text-[14px] font-semibold text-slate-900 truncate" style={{ fontFamily: "'Libre Baskerville', serif" }}>{g.intake_label}</h4>
                <Badge className="bg-slate-100 text-slate-700 border-slate-200 border text-[10.5px] font-semibold tabular-nums">{g.count} signed</Badge>
                {g.downloadable_count < g.count && (
                  <Badge className="bg-amber-50 text-amber-800 border-amber-300 border text-[10.5px] font-medium">
                    <WarningCircle size={11} className="mr-1" /> {g.count - g.downloadable_count} missing PDF
                  </Badge>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); bulkDownloadIntake(g); }}
                disabled={busy || g.downloadable_count === 0}
                className="h-8 px-3 text-[11.5px] font-medium border-indigo-300 text-indigo-800 hover:bg-indigo-50 rounded-md disabled:opacity-50"
                data-testid={`bulk-download-signed-eas-${g.intake_label.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                title={g.downloadable_count === 0 ? 'No downloadable PDFs in this intake' : `Download ${g.downloadable_count} signed EA${g.downloadable_count === 1 ? '' : 's'} as a single ZIP`}
              >
                {busy ? <CircleNotch size={12} className="mr-1.5 animate-spin" /> : <Archive size={12} className="mr-1.5" />}
                {busy ? `${job.done}/${job.total} packing…` : `Download all (${g.downloadable_count})`}
              </Button>
            </header>

            {open && (
              <div className="divide-y divide-slate-100 border-t border-slate-200">
                {g.students.map(s => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50/60 transition"
                    data-testid={`signed-ea-row-${s.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-[13px] font-medium text-slate-900 truncate">{s.full_name}</p>
                        {s.ea_source === 'manual_upload' ? (
                          <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 border text-[9.5px] font-semibold uppercase tracking-wide">Manual</Badge>
                        ) : s.ea_source === 'docusign' ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[9.5px] font-semibold uppercase tracking-wide">DocuSign</Badge>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-[9.5px] font-semibold uppercase tracking-wide">No PDF</Badge>
                        )}
                      </div>
                      <p className="text-[11.5px] text-slate-500 mt-0.5 truncate">
                        {s.email}
                        {s.ea_signed_at && <> · signed {new Date(s.ea_signed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</>}
                        {s.recruiter_name && <> · {s.recruiter_name}</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {s.has_downloadable_ea ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadOne(s)}
                          className="h-7 px-2.5 text-[11px] font-medium border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-md"
                          data-testid={`download-signed-ea-${s.id}`}
                        >
                          <DownloadSimple size={11} className="mr-1" /> Download
                        </Button>
                      ) : (
                        <label
                          className="inline-flex items-center h-7 px-2.5 text-[11px] font-medium border border-indigo-300 text-indigo-800 hover:bg-indigo-50 rounded-md cursor-pointer transition"
                          data-testid={`upload-signed-ea-${s.id}`}
                          title="Attach the signed PDF (bulk-marked students have no DocuSign envelope)"
                        >
                          <UploadSimple size={11} className="mr-1" /> Upload
                          <input
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              e.target.value = '';
                              if (f) uploadOne(s, f);
                            }}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
